import { Component, Input, OnInit, SimpleChange } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Dropdown } from 'app/core/model/common/dropdown.model';
import { CommonService } from 'app/core/services/common.service';
import { Observable } from 'rxjs';
import { formatDate } from '@angular/common';
import saveAs from 'file-saver';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-export-data',
  templateUrl: './export-data.component.html',
  styleUrls: ['./export-data.component.scss'],
})
export class ExportDataComponent implements OnInit {
  @Input() report_type: string = ''; // Type of report to be exported
  id: number = 0; // Identifier for different report types
  fields: any[]; // Array to hold the fields for export
  userList$: Observable<Dropdown[]>; // Observable for the user dropdown list
  form: FormGroup; // Form group for the dynamic form fields
  today = new Date(); // Current date
  start_date: any; // Selected start date
  end_date: any; // Selected end date
  file_name: any; // Name of the exported file

  toggled: boolean = true; // Flag to toggle additional options
  minToDate: Date; // Minimum selectable date for 'To Date'
  maxFromDate: Date; // Maximum selectable date for 'From Date'
  startDateError: boolean = false; // Flag for start date validation error
  endDateError: boolean = false; // Flag for end date validation error

  constructor(
    private fb: FormBuilder,
    private commonService: CommonService,
    private toastrService: ToastrService
  ) {}

  ngOnInit() {
    // this.initializeForm();
    // this.setInitialDates();
    // this.setReportTypeData();
    // this.loadUsers();
    // this.loadField();
  }

  ngOnChanges(changes: SimpleChange){
    if (changes['report_type'].currentValue){
      this.initializeForm();
      this.setInitialDates();
      this.setReportTypeData();
      this.loadUsers();
      this.loadField();
    }
  }

  /**
   * Initializes the form group.
   */
  private initializeForm() {
    this.form = this.fb.group({});
  }

  /**
   * Sets the initial start and end dates for the date pickers.
   */
  private setInitialDates() {
    this.start_date = new Date(
      this.today.getFullYear(),
      this.today.getMonth(),
      this.today.getDate() - 30
    );
    this.minToDate = this.start_date;
    this.end_date = this.today;
    this.maxFromDate = this.end_date;
  }

  /**
   * Sets the report ID and file name based on the report type.
   */
  private setReportTypeData() {
    const reportTypeMap: { [key: string]: { id: number; fileName: string } } = {
      expense: { id: 1, fileName: 'expense' },
      moneyrequest: { id: 2, fileName: 'moneyrequest' },
      report: { id: 3, fileName: 'report' },
      transaction: { id: 4, fileName: 'transaction' },
      all: { id: 6, fileName: 'all_txn' },
      reverse_expense: { id: 7, fileName: 'reverse_expense' },
    };

    const reportData = reportTypeMap[this.report_type];
    if (reportData) {
      this.id = reportData.id;
      this.file_name = reportData.fileName;
    }
  }
  /**
   * Creates the dynamic form controls based on the fields array.
   */
  private createForms() {
    const formControls = {};
    for (const item of this.fields) {
      formControls[item.value] = [item.status];
    }
    this.form = this.fb.group(formControls);
  }
  /**
   * Loads the list of users for the user dropdown.
   */
  private loadUsers() {
    this.userList$ = this.commonService.getUserDropdown();
  }

  /**
   * Loads the fields for export based on the report ID.
   */
  private loadField() {
    this.commonService.getExportField(this.id).subscribe((res) => {
      this.fields = res;
      this.createForms();
      this.commonService.resetLoader();
    });
  }

  /**
   * Resets the filter to default values.
   */
  resetFilter() {
    const formControls = {};
    for (const item of this.fields) {
      formControls[item.value] = true;
    }
    this.form = this.fb.group(formControls);
    this.start_date = new Date();
    this.end_date = new Date();
  }

  /**
   * Handles the form submission for exporting data.
   */
  onSubmit(): void {
    this.updateFieldStatus();

    const startDate = new Date(this.start_date);
    const endDate = new Date(this.end_date);
    const diffInDays = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

    const exportData = {
      id: this.id,
      start_date: formatDate(startDate, 'yyyy-MM-dd', 'en'),
      end_date: formatDate(endDate, 'yyyy-MM-dd', 'en'),
      fields: this.fields,
    };

    this.commonService.exportData(exportData, diffInDays).subscribe((response: any) => {
      if (diffInDays >= 14) {
        this.toastrService.success('CSV file will be sent to your email');
      } else {
        this.downloadCsv(response);
      }
    });
  }

  /**
   * Updates the status of each field based on the form control values.
   */
  private updateFieldStatus() {
    this.fields.forEach((item) => {
      const formControlName = item.value;
      item.status = this.form.get(formControlName).value;
    });
  }

  /**
   * Downloads the CSV file.
   * @param response - The CSV data.
   */
  private downloadCsv(response: any) {
    const blob = new Blob([response], { type: 'text/csv' });
    const filename = this.file_name + '.csv';
    saveAs(blob, filename);
  }

  /**
   * Handles changes to the 'From Date' picker.
   * @param event - The date change event.
   */
  onFromDateChange(event) {
    this.minToDate = event;
    this.startDateError = this.start_date > this.today;
    if (!this.startDateError && this.start_date && this.end_date) {
      this.adjustEndDate();
    }
    this.updateMaxFromDate();
    this.endDateError = this.end_date > this.today;
  }

  /**
   * Adjusts the end date based on the start date if the difference is more than 30 days.
   */
  private adjustEndDate() {
    const start = new Date(this.start_date);
    const end = new Date(this.end_date);
    const diffInDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays > 30) {
      const newEnd = new Date(start);
      newEnd.setDate(newEnd.getDate() + 30);
      this.end_date = newEnd;
    }
  }
  /**
   * Handles changes to the 'To Date' picker.
   * @param event - The date change event.
   */
  onToDateChange(event) {
    this.maxFromDate = event;
    this.endDateError = this.end_date > this.today;
    if (!this.endDateError && this.start_date && this.end_date) {
      this.adjustStartDate();
    }
    this.updateMinToDate();
  }

  /**
   * Adjusts the start date based on the end date if the difference is more than 30 days.
   */
  private adjustStartDate() {
    const start = new Date(this.start_date);
    const end = new Date(this.end_date);
    const diffInDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays > 30) {
      const newStart = new Date(end);
      newStart.setDate(newStart.getDate() - 30);
      this.start_date = newStart;
    }
  }

  /**
   * Updates the minimum selectable date for the 'To Date' picker.
   */
  private updateMinToDate() {
    if (this.start_date) {
      this.minToDate = new Date(this.start_date);
    }
  }

  /**
   * Updates the maximum selectable date for the 'From Date' picker.
   */
  private updateMaxFromDate() {
    if (this.end_date) {
      this.maxFromDate = new Date(this.end_date);
    }
  }
}
